import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Badge, Button, EmptyState, Field, Input, Select } from "@agentsaas/ui";
import { Loading } from "../layout/Loading";
import { pickLocalized, Agent } from "@agentsaas/protocol";
import type { Agent as AgentT } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { qk, useCredentials, useProject, useTemplates } from "../data/queries";

/** 从模板创建 agent：模板名称与描述按当前语言显示 */
export function NewAgentPage() {
  const { pid = "" } = useParams();
  const { t, locale } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const project = useProject(pid);
  const [sp] = useSearchParams();
  // 内置 + 本租户自定义模板
  const templates = useTemplates(project.data?.tenantId, { enabled: !!project.data });
  const creds = useCredentials(project.data?.tenantId);
  // 凭据池排在前面（默认选中）：有多个订阅账号时自动分配、限流时自动切换
  const usable = (creds.data ?? []).filter((c) => c.status === "valid" || c.status === "unverified").sort((a, b) => Number(b.kind === "pool") - Number(a.kind === "pool"));
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [credentialId, setCredentialId] = useState("");
  const [budget, setBudget] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const preset = sp.get("template");
  const tpl = templateId ?? (preset && templates.data?.some((x) => x.id === preset) ? preset : null) ?? templates.data?.[0]?.id ?? null;
  const cred = credentialId || usable[0]?.id || "";
  const nameOk = Agent.shape.name.safeParse(name).success;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tpl || !cred || !nameOk) return;
    setBusy(true);
    setError(null);
    try {
      const usd = budget.trim() ? Number(budget) : null;
      const a = await api.call("agentCreate", {
        params: { pid },
        body: { templateId: tpl, name, credentialId: cred, monthlyLimitMicroUsd: usd === null || Number.isNaN(usd) ? null : Math.round(usd * 1_000_000) },
      });
      qc.setQueryData<AgentT[]>(qk.agents(pid), (xs) => (xs ? [...xs.filter((x) => x.id !== a.id), a] : [a]));
      navigate(`/p/${pid}/a/${a.id}`, { replace: true });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (templates.isPending || project.isPending) return <Loading />;
  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">{t("agent.create.title")}</h1>
        <Link to={`/p/${pid}`}>{t("common.back")}</Link>
      </div>
      <form onSubmit={submit} className="stack">
        <fieldset className="tpl-grid" aria-label={t("agent.create.template")}>
          <legend className="ui-field__label">{t("agent.create.template")}</legend>
          {templates.data?.map((x) => (
            <label key={x.id} className={`tpl-card${tpl === x.id ? " is-active" : ""}`} data-testid="template-card">
              <input type="radio" name="template" className="ui-sr-only" checked={tpl === x.id} onChange={() => setTemplateId(x.id)} />
              <strong className="row">
                <span className="grow">{pickLocalized(x.name, locale)}</span>
                {x.tenantId ? <Badge tone="accent">{t("template.custom")}</Badge> : null}
              </strong>
              <span className="muted small">{pickLocalized(x.description, locale)}</span>
            </label>
          ))}
        </fieldset>
        <div className="card">
          <Field label={t("agent.create.name")} hint={t("agent.create.name_hint")} error={name && !nameOk ? t("error.common.validation_failed") : undefined}>
            {(id, d) => <Input id={id} aria-describedby={d} value={name} onChange={(e) => setName(e.target.value)} maxLength={32} data-testid="agent-name" />}
          </Field>
          {usable.length ? (
            <Field label={t("agent.create.credential")}>
              {(id) => (
                <Select id={id} value={cred} onChange={(e) => setCredentialId(e.target.value)}>
                  {usable.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label} · {t(`credential.kind.${c.kind}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : (
            <EmptyState icon="key" title={t("agent.create.no_credential")}>
              {project.data ? <Link to={`/t/${project.data.tenantId}/credentials`}>{t("credential.add")}</Link> : null}
            </EmptyState>
          )}
          <Field label={t("agent.create.budget")}>
            {(id) => <Input id={id} type="number" inputMode="decimal" min={0} step="0.01" value={budget} onChange={(e) => setBudget(e.target.value)} />}
          </Field>
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" variant="primary" loading={busy} disabled={!tpl || !cred || !nameOk} data-testid="agent-submit">
            {t("agent.create.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}
